import { useEffect, useState } from "react";
import { Divider, PageHeader, Tabs } from "antd";
import LayoutCss from "layout/layout.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import Client from "redux/models/client";
import { selectClient } from "redux/slices/clientSlice";
import K from "utilities/constants";
import detailsConfig from "config/detailConfig";
import { isPermissionPresent } from "utilities/generalUtility";
import ClientBillingRules from "./clientBillingRules/clientBillingRules";
import "./details.scss";
import JobBillingRules from "./jobBillingRules/jobBillingRules";
import ClientJobs from "./jobs/clientJobs";
import Overview from "./Overview/overview";
import { selectUser } from "redux/slices/userSlice";
import ClientLookupTablesContainer from "./clientLookupTables/clientLookupTablesContainer";
import ClientHiringManagers from "./hiringManagers/hiringManager";
import UserDefinedFields from "./userDefinedFields/userDefinedFields";
import UDF from "redux/models/udf";
import { setClientUdf } from "redux/slices/udfSlice";
import Attachments from "./attachments/attachments";
import { ClientClassesContainer } from "./classes/clientClassesContainer/clientClassesContainer";
import { InvoicesContainer } from "./invoices/InvoicesContainer";

export default function ClientDetails() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const userSlice = useSelector(selectUser);

  const clientSlice = useSelector(selectClient);
  const [shouldLoad, setShouldLoad] = useState(false);
  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    const currentKey = params.get("tab");
    if (!(currentKey in detailsConfig)) {
      history.push({ search: "?tab=overview" });
    }

    (async () => {
      try {
        await dispatch(Client.getById(id));
        await getClientEditUdfs();
        setShouldLoad(true);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const getClientEditUdfs = async () => {
    const clientUdfs = await UDF.getEditUdfFields({
      clientId: id,
      entityId: K.UDF.Entities.Client.id,
      entityKey: K.UDF.Entities.Client.key,
      entityKeyId: id,
      isVisible: 1,
    });
    dispatch(setClientUdf(clientUdfs));
  };

  const tabComponents = {
    overview: <Overview clientData={clientSlice[K.Redux.SelectedClient]} />,
    jobs: <ClientJobs />,
    "client-billing-rules": <ClientBillingRules />,
    "client-invoices": (
      <InvoicesContainer
        key="client-invoices"
        type={K.Invoice.Type.Client}
        clientIds={[id]}
        selectedClientId={id}
        filtersPrefix={`client_${id}_${K.Invoice.Type.Client}_invoices`}
        tableConfigKey={K.AgGridTable.Keys.ClientInvoicesVisibleColumn}
      />
    ),
    "job-invoices": (
      <InvoicesContainer
        key="job-invoices"
        type={K.Invoice.Type.Job}
        clientIds={[id]}
        selectedClientId={id}
        filtersPrefix={`client_${id}_${K.Invoice.Type.Job}_invoices`}
        tableConfigKey={K.AgGridTable.Keys.ClientJobInvoicesVisibleColumn}
      />
    ),
    "job-billing-rules": <JobBillingRules />,
    lookup: <ClientLookupTablesContainer clientId={id} />,
    "hiring-manager": <ClientHiringManagers />,
    "user-defineds": <UserDefinedFields />,
    attachments: <Attachments />,
    class: <ClientClassesContainer />,
  };

  const onChange = (key) => {
    params.set("tab", key);
    params.delete("table");
    history.replace({ search: params.toString() });
  };

  return (
    <>
      <PageHeader
        ghost={false}
        className={LayoutCss.appPageHeader}
        onBack={() => history.goBack()}
        title="Client Details"
        footer={
          <>
            <Divider className={LayoutCss.appPageHeaderDivider} />
            <Tabs
              activeKey={params.get("tab")}
              className={LayoutCss.appPageHeaderTabs}
              items={Object.keys(detailsConfig)
                .map((item) => ({
                  key: item,
                  label: detailsConfig[item].name,
                  permission: detailsConfig[item].permission,
                }))
                .filter(({ permission }) =>
                  isPermissionPresent(permission, userSlice.roles),
                )}
              onChange={onChange}
            />
          </>
        }
      />

      {shouldLoad &&
        isPermissionPresent(
          detailsConfig[params.get("tab")].permission,
          userSlice.roles,
        ) &&
        tabComponents[params.get("tab")]}
    </>
  );
}
